import type { Approval, IntegrationItem, ModuleItem, WorkflowItem } from './types'

export const navModules = [
  'Business Brain',
  'Founder Command Centre',
  'Campaign & Creative Engine',
  'Sales & Customer Intelligence',
  'Market Intelligence',
  'Digital Product Factory',
  'Website & Commerce Optimiser',
  'Operations Agent',
  'Case Study Evidence',
]

export const workflows: WorkflowItem[] = [
  {
    id: 'di-founder-command-brief',
    name: 'Daily founder command brief',
    outcome: 'Leadership clarity and exception management',
    module: 'Founder Command Centre',
    schedule: 'Weekdays, 07:00',
    approvals: 0,
    estimatedUsage: 'Budget-controlled',
    status: 'ready',
  },
  {
    id: 'di-stack-health-watch',
    name: 'Stack health and scheduled-job watchdog',
    outcome: 'Earlier failure detection and recovery evidence',
    module: 'Operations Agent',
    schedule: 'Every 15 minutes',
    approvals: 0,
    estimatedUsage: 'Low',
    status: 'ready',
  },
  {
    id: 'di-business-brain-sync',
    name: 'Business Brain source sync and conflict review',
    outcome: 'Governed organisational memory',
    module: 'Business Brain',
    schedule: 'Every 6 hours',
    approvals: 1,
    estimatedUsage: 'Low–medium',
    status: 'ready',
  },
  {
    id: 'di-market-pain-point-watch',
    name: 'Market, competitor and pain-point intelligence',
    outcome: 'Evidence-backed opportunities',
    module: 'Market Intelligence',
    schedule: 'Mondays and Thursdays',
    approvals: 1,
    estimatedUsage: 'Medium',
    status: 'ready',
  },
  {
    id: 'di-product-readiness-qa',
    name: 'Physical and digital product readiness QA',
    outcome: 'Fewer release defects',
    module: 'Digital Product Factory',
    schedule: 'Every 4 hours',
    approvals: 1,
    estimatedUsage: 'Medium',
    status: 'ready',
  },
  {
    id: 'di-product-catalogue-integrity',
    name: 'Product catalogue and collection integrity',
    outcome: 'Accurate products, links and collections',
    module: 'Website & Commerce Optimiser',
    schedule: 'Every 6 hours',
    approvals: 1,
    estimatedUsage: 'Low',
    status: 'ready',
  },
  {
    id: 'di-weekly-case-study-evidence',
    name: 'Weekly case-study evidence collection',
    outcome: 'Verified before-and-after proof',
    module: 'Case Study Evidence',
    schedule: 'Fridays, 17:00',
    approvals: 1,
    estimatedUsage: 'Low',
    status: 'ready',
  },
  {
    id: 'di-fortnightly-newsletter',
    name: 'Fortnightly newsletter production',
    outcome: 'Consistent owned-audience communication',
    module: 'Campaign & Creative Engine',
    schedule: '1st and 15th of each month',
    approvals: 1,
    estimatedUsage: 'Medium',
    status: 'draft',
  },
  {
    id: 'di-customer-enquiry-triage',
    name: 'Customer enquiry triage and response drafting',
    outcome: 'Faster, more consistent customer response preparation',
    module: 'Sales & Customer Intelligence',
    schedule: 'Every 30 minutes, business hours',
    approvals: 1,
    estimatedUsage: 'Low–medium',
    status: 'draft',
  },
]

export const initialApprovals: Approval[] = [
  {
    id: 'gate-001',
    type: 'Pilot gate',
    title: 'Approve the private source register before first sync',
    summary: 'Confirm the approved Drive, Sheets, website and asset-library sources. Private identifiers remain outside GitHub.',
    requestedBy: 'Business Brain',
    risk: 'Medium',
    cost: 'No external spend',
    status: 'pending',
    due: 'Before first production sync',
  },
  {
    id: 'gate-002',
    type: 'Security gate',
    title: 'Verify Cloudflare Access policy for Tenant 001',
    summary: 'Restrict the pilot API to the founder and explicitly approved operators before remote deployment.',
    requestedBy: 'DAWN Control Plane',
    risk: 'Medium',
    cost: 'No external spend',
    status: 'pending',
    due: 'Before remote deployment',
  },
  {
    id: 'gate-003',
    type: 'Evidence gate',
    title: 'Capture the 14-day manual-process baseline',
    summary: 'Record current task durations, failure rates, review effort and output quality before automation results are compared.',
    requestedBy: 'Case Study Evidence',
    risk: 'Low',
    cost: 'No external spend',
    status: 'pending',
    due: 'Before workflow outcome claims',
  },
  {
    id: 'gate-004',
    type: 'Stage 2 gate',
    title: 'Keep customer enquiry drafting disabled until mailbox scope is confirmed',
    summary: 'The connected Gmail account did not return customer messages addressed to the Cloudflare aliases. Do not enable drafting until routing and account scope are verified.',
    requestedBy: 'Sales & Customer Intelligence',
    risk: 'Medium',
    cost: 'No external spend',
    status: 'pending',
    due: 'Before Stage 2',
  },
]

export const initialModules: ModuleItem[] = [
  {
    id: 'brain',
    name: 'Business Brain',
    description: 'Governs the approved brand, product, commercial and operating knowledge available to every workflow.',
    enabled: true,
    tier: 'Pilot Stage 1',
    permissions: ['Read approved sources', 'Flag stale facts', 'Route conflicts for approval'],
  },
  {
    id: 'founder',
    name: 'Founder Command Centre',
    description: 'Surfaces priorities, exceptions, failed jobs, approvals, usage and decisions requiring founder attention.',
    enabled: true,
    tier: 'Pilot Stage 1',
    permissions: ['Read workflow state', 'Summarise exceptions', 'No external actions'],
  },
  {
    id: 'creative',
    name: 'Campaign & Creative Engine',
    description: 'Prepares campaigns, newsletters, content and creative manifests from the approved Deus Intus brain.',
    enabled: true,
    tier: 'Stage 1 QA / Stage 2 drafts',
    permissions: ['Inspect assets', 'Create drafts', 'Publishing disabled'],
  },
  {
    id: 'customer',
    name: 'Sales & Customer Intelligence',
    description: 'Prepares enquiry classification, research and response drafts after mailbox scope is verified.',
    enabled: false,
    tier: 'Pilot Stage 2',
    permissions: ['Metadata and drafts only', 'Sending disabled', 'Mailbox gate required'],
  },
  {
    id: 'market',
    name: 'Market Intelligence',
    description: 'Tracks competitor movement, customer language and pain-point evidence without turning weak signals into claims.',
    enabled: true,
    tier: 'Pilot Stage 1',
    permissions: ['Read public sources', 'Cite evidence', 'Claims require approval'],
  },
  {
    id: 'products',
    name: 'Digital Product Factory',
    description: 'Validates product readiness now and prepares staged digital-product builds after the first safety gates pass.',
    enabled: true,
    tier: 'Stage 1 QA / Stage 2 production',
    permissions: ['Validate queue and files', 'Prepare staged outputs', 'Release requires approval'],
  },
  {
    id: 'commerce',
    name: 'Website & Commerce Optimiser',
    description: 'Checks catalogue integrity and prepares conversion or merchandising changes without deploying them.',
    enabled: true,
    tier: 'Pilot Stage 1',
    permissions: ['Read approved catalogue', 'Prepare change briefs', 'Publishing disabled'],
  },
  {
    id: 'ops',
    name: 'Operations Agent',
    description: 'Monitors scheduled work, failures, recovery evidence, budgets and system health.',
    enabled: true,
    tier: 'Pilot Stage 1',
    permissions: ['Monitor jobs', 'Create incidents', 'Respect global kill switches'],
  },
  {
    id: 'evidence',
    name: 'Case Study Evidence',
    description: 'Captures baselines, time saved, quality, costs, incidents, outcomes and publication decisions.',
    enabled: true,
    tier: 'Pilot Stage 1',
    permissions: ['Record evidence', 'Human verification required', 'Founder approval before publication'],
  },
]

export const initialIntegrations: IntegrationItem[] = [
  { id: 'gdrive', name: 'Google Drive', category: 'Google', connected: false, owner: 'Pilot setup', lastSync: 'Secure source connection pending', scopes: ['Selected files and folders: read only'] },
  { id: 'gsheets', name: 'Google Sheets', category: 'Google', connected: false, owner: 'Pilot setup', lastSync: 'Production queue connection pending', scopes: ['Selected spreadsheet: read only'] },
  { id: 'gmail', name: 'Gmail / Cloudflare aliases', category: 'Google', connected: false, owner: 'Stage 2 gate', lastSync: 'Mailbox routing and account scope unverified', scopes: ['Metadata and draft creation only'] },
  { id: 'site', name: 'Website and catalogue', category: 'Website / Commerce', connected: false, owner: 'Pilot setup', lastSync: 'Read connector pending', scopes: ['Public pages and catalogue: read only'] },
  { id: 'social', name: 'Social channels', category: 'Social / Advertising', connected: false, owner: 'Kill switch engaged', lastSync: 'Publishing disabled', scopes: ['Research and draft preparation only'] },
  { id: 'creative', name: 'Creative and asset tools', category: 'Creative', connected: false, owner: 'Pilot setup', lastSync: 'Approved asset-library connection pending', scopes: ['Approved assets: read', 'Draft generation only'] },
  { id: 'cloudflare', name: 'Cloudflare control plane', category: 'Infrastructure', connected: false, owner: 'Deployment gate', lastSync: 'Worker and D1 code ready; not remotely deployed', scopes: ['Access-protected API', 'D1', 'Workflows', 'Cron'] },
  { id: 'hermes', name: 'Private orchestration service', category: 'Custom', connected: false, owner: 'Later implementation', lastSync: 'Service binding not connected', scopes: ['Stable DAWN execution contract only'] },
]

export const knowledgeSources = [
  { name: 'Deus Intus Brand Operating System', type: 'Google Drive', owner: 'Founder', status: 'Connection pending', synced: 'Not yet synced', permissions: 'Internal · read only' },
  { name: 'Deus Intus product blueprint', type: 'Google Drive', owner: 'Commercial', status: 'Connection pending', synced: 'Not yet synced', permissions: 'Internal · read only' },
  { name: 'Digital Product Factory master directive', type: 'Google Drive', owner: 'Product Operations', status: 'Connection pending', synced: 'Not yet synced', permissions: 'Restricted · read only' },
  { name: 'Digital Product Factory production queue', type: 'Google Sheets', owner: 'Product Operations', status: 'Connection pending', synced: 'Not yet synced', permissions: 'Restricted · read only' },
  { name: 'Approved website and product catalogue', type: 'Website', owner: 'Commerce', status: 'Connection pending', synced: 'Not yet synced', permissions: 'Public · read only' },
  { name: 'Approved image and asset library', type: 'Google Drive', owner: 'Creative Operations', status: 'Connection pending', synced: 'Not yet synced', permissions: 'Restricted · read only' },
  { name: 'Customer enquiry mailbox', type: 'Gmail', owner: 'Customer Operations', status: 'Disabled pending scope review', synced: 'Not connected', permissions: 'Confidential · metadata and drafts only' },
]
